import React, { useEffect, useRef } from "react";
import * as Chart from "chart.js";

const CHART_HEIGHT = 260;

const AreaChart = ({
  data = [],
  metrics = [],
  headerBadge = "",
  mainTitle = "",
  yAxisLabel = "MONTHLY DAILY VOLUMES",
  firstColumnLabel = "",
  tableRowLabel = "",
  areaStartColor = "var(--green-medium)",
  areaEndColor = "var(--green-medium)",
  headerBgColor = "var(--green-medium)",
  sidebarBgColor = "var(--!border-blue)",
  sidebarBorderColor = "var(--!border-blue)",
  title,
  subTitle,
  tableHeaderFontSize = "6px",   // time labels (00, 01, 02...)
tableValueFontSize = "6px",    // values (1,234)
tableLabelFontSize = "6px",    // "Collection" label
}) => {
  const chartRef = useRef(null);
  const chartInstanceRef = useRef(null);
  const containerRef = useRef(null);

  const buildChart = (width, height) => {
    if (!chartRef.current || data.length === 0) return;

    if (chartInstanceRef.current) {
      chartInstanceRef.current.destroy();
      chartInstanceRef.current = null;
    }

    // ✅ Manually set canvas pixel dimensions — bypass transform confusion
    chartRef.current.width = width;
    chartRef.current.height = height;

    const ctx = chartRef.current.getContext("2d");

    const startColor =
      getComputedStyle(document.documentElement)
        .getPropertyValue(areaStartColor.replace("var(", "").replace(")", ""))
        .trim() || areaStartColor;

    const endColor =
      getComputedStyle(document.documentElement)
        .getPropertyValue(areaEndColor.replace("var(", "").replace(")", ""))
        .trim() || areaEndColor;

    const gradient = ctx.createLinearGradient(0, 0, 0, height);
    gradient.addColorStop(0, startColor + "F2");
    gradient.addColorStop(1, endColor + "66");

   
    const ROW_LABEL_WIDTH_PCT = 0.06;
    const rowLabelWidth = width * ROW_LABEL_WIDTH_PCT;
    const dataColWidth = (width - rowLabelWidth) / data.length;
    const halfColWidth = dataColWidth / 2;

    Chart.Chart.register(
      Chart.LineController,
      Chart.LineElement,
      Chart.PointElement,
      Chart.LinearScale,
      Chart.CategoryScale,
      Chart.Filler,
      Chart.Tooltip,
      Chart.Title
    );

    const textColor = "#e5e7eb";

    const dataLabelsPlugin = {
  id: "dataLabels",
  afterDatasetsDraw: (chart) => {
    const ctx = chart.ctx;
    const chartArea = chart.chartArea;

    chart.data.datasets.forEach((dataset, datasetIndex) => {
      const meta = chart.getDatasetMeta(datasetIndex);

      const points = meta.data
        .map((point, index) => ({
          x: Math.min(
            Math.max(point.x, chartArea.left + 20),
            chartArea.right - 20
          ),
          y: point.y,
          value: dataset.data[index],
          index,
        }))
        .filter((p) => p.value != null && p.value > 0);

      if (!points.length) return;

      // Pehle font set karo taaki measureText accurate ho
      ctx.font = "10px sans-serif";

      // Har point ka actual text width measure karo
      const pointsWithWidth = points.map((p) => ({
        ...p,
        label: p.value.toLocaleString(),
        halfW: ctx.measureText(p.value.toLocaleString()).width / 2 + 2,
      }));

      const OFFSETS = [-40, -55, -25, -70, -85, -100, -15];
      const MIN_DY  = 12;
      const placed  = []; // { x, labelY, halfW }

      // Do rectangles overlap karte hain?
      const overlaps = (ax, ay, aHalfW, bx, by, bHalfW) =>
        Math.abs(ax - bx) < (aHalfW + bHalfW) &&
        Math.abs(ay - by) < MIN_DY;

      pointsWithWidth.forEach((p) => {
        let chosenY = null;

        for (const offset of OFFSETS) {
          const candidateY = p.y + offset;
          if (candidateY < chartArea.top + 5) continue;

          const clash = placed.some((pl) =>
            overlaps(p.x, candidateY, p.halfW, pl.x, pl.labelY, pl.halfW)
          );

          if (!clash) {
            chosenY = candidateY;
            break;
          }
        }

        if (chosenY === null) chosenY = chartArea.top + 10;

        placed.push({ x: p.x, labelY: chosenY, halfW: p.halfW });

        ctx.save();
        ctx.fillStyle    = "#ffffff";
        ctx.font         = "10px sans-serif";
        ctx.textAlign    = "center";
        ctx.textBaseline = "bottom";
        ctx.fillText(p.label, p.x, chosenY);
        ctx.restore();
      });
    });
  },
};

    chartInstanceRef.current = new Chart.Chart(ctx, {
      type: "line",
      data: {
        labels: data.map((d) => d.time),
        datasets: [
          {
            data: data.map((d) => d.value),
            borderColor: "rgba(0,0,0,0)",
            backgroundColor: gradient,
            borderWidth: 1.5,
            fill: true,
            tension: 0.4,
            pointRadius: 0,
            pointHoverRadius: 5,
            pointBackgroundColor: startColor,
            pointBorderColor: "#ffffff",
            pointBorderWidth: 1,
            categoryPercentage: 1.0,
            barPercentage: 1.0,
            clip: false,
          },
        ],
      },
      plugins: [dataLabelsPlugin],
      options: {
        // ✅ responsive: false — hum khud size control kar rahe hain
        responsive: false,
        maintainAspectRatio: false,
        animation: false,
                devicePixelRatio: Math.max((window.devicePixelRatio || 1) * 2, 3),
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: "rgba(30, 41, 59, 0.95)",
            titleColor: "#ffffff",
            bodyColor: "#ffffff",
            borderColor: "#475569",
            borderWidth: 1,
            padding: 12,
            displayColors: false,
            callbacks: {
              title: (context) => context[0].label,
              label: (context) =>
                `Value: ${context.parsed.y.toLocaleString()}`,
            },
          },
        },
        scales: {
          x: { display: false, offset: false },
          y: {
            // ✅ y-axis ki width table ke 8% row-label column jitni fix
            afterFit: (scaleInstance) => {
              scaleInstance.width = rowLabelWidth;
            },
            ticks: {
              color: textColor,
              font: { size: 8 },
              callback: (value) => value.toLocaleString(),
            },
            grid: { display: false },
            afterDataLimits: (scale) => {
              scale.max = scale.max * 1.15;
            },
            title: {
              display: true,
              text: yAxisLabel,
              color: textColor,
              font: { size: 8, weight: "300" },
            },
          },
        },
        layout: {
          // ✅ left/right padding = ek table column ki half-width,
          // isse har data-point apne column ke center ke exact upar align hota hai
          padding: {
            left: halfColWidth,
            right: halfColWidth,
            top: 35,
            bottom: 0,
          },
        },
      },
    });
  };

  useEffect(() => {
    if (!containerRef.current || data.length === 0) return;

    // ✅ offsetWidth/offsetHeight: transform-independent actual DOM size
    // getBoundingClientRect() scale factor include karta hai — isliye use nahi karna
    const w = containerRef.current.offsetWidth;
    const h = CHART_HEIGHT;

    buildChart(w, h);

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        // offsetWidth use karo — scale transform ko ignore karta hai
        const newW = entry.target.offsetWidth;
        const newH = CHART_HEIGHT;
        if (!newW) continue;

        if (chartInstanceRef.current) {
          chartInstanceRef.current.destroy();
          chartInstanceRef.current = null;
        }
        buildChart(newW, newH);
      }
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
        chartInstanceRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, areaStartColor, areaEndColor, yAxisLabel]);

  return (
    <div
      className="text-foreground transition-colors w-full"
      style={{ backgroundColor: "#464647" }}
    >
      {title || subTitle ? (
        <div
          className="text-center"
          style={{ backgroundColor: "#ffffff", padding: "1rem" }}
        >
          <h1 className="text-2xl font-bold" style={{ color: "#000000" }}>
            {title}
          </h1>
          {subTitle && (
            <h2 className="text-xl font-bold" style={{ color: "#000000" }}>
              {subTitle}
            </h2>
          )}
        </div>
      ) : null}

      <div className="w-full flex flex-col p-3" style={{ minHeight: 0 }}>
        <div
          className="relative flex items-start justify-between mb-2"
          style={{ minHeight: "2rem" }}
        >
          {headerBadge && (
            <div
              className="px-2 py-1 rounded text-xs font-semibold text-white flex-shrink-0"
              style={{ backgroundColor: headerBgColor }}
            >
              {headerBadge}
            </div>
          )}

          <div
            className="absolute left-0 right-0 text-center text-sm font-bold pointer-events-none"
            style={{ color: "#ffffff", top: 0 }}
          >
            {mainTitle}
          </div>

          {metrics.length > 0 && (
            <div
              className="ml-auto text-xs rounded shadow-sm flex-shrink-0"
              style={{ borderColor: sidebarBorderColor }}
            >
              <table className="!border-collapse">
                <tbody>
                  {metrics.map((m, i) => (
                    <tr key={i}>
                      <td
                        className="px-2 py-1 !border font-medium"
                        style={{
                          borderColor: sidebarBorderColor,
                          backgroundColor: "#464647",
                          color: "#ffffff",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {m.label}
                      </td>
                      <td
                        className="px-2 py-1 !border font-bold"
                        style={{
                          borderColor: sidebarBorderColor,
                          backgroundColor: "#464647",
                          color: "#ffffff",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {m.value}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/*
          ✅ containerRef yahan — offsetWidth se actual unscaled width milti hai
          canvas ko CSS width/height mat do — Chart.js canvas.width/height se control karta hai
        */}
        <div
          ref={containerRef}
          style={{
            height: `${CHART_HEIGHT}px`,
            width: "100%",
            position: "relative",
          }}
        >
          <canvas ref={chartRef} />
        </div>

        <div className="w-full" style={{ marginTop: "4px" }}>
          <table
            style={{
              borderCollapse: "collapse",
              width: "100%",
              tableLayout: "fixed",
            }}
          >
            <colgroup>
              <col style={{ width: "8%" }} />
              {data.map((_, i) => (
                <col key={i} style={{ width: `${92 / data.length}%` }} />
              ))}
            </colgroup>
            <thead>
              <tr>
                <th
  style={{
    color: "#ffffff",
    border: "1px solid #ffffff",
    padding: "3px 2px",
    textAlign: "left",
    fontSize: tableLabelFontSize,
    fontWeight: 600,
  }}
>
  {firstColumnLabel}
</th>
                {data.map((d, i) => (
                  <th
                    key={i}
                    style={{
                      color: "#ffffff",
                      border: "1px solid #ffffff",
                      padding: "3px 0px",
                      textAlign: "center",
                      fontSize: tableHeaderFontSize,

                      fontWeight: 500,
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {d.time}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                <td
                  style={{
                    border: "1px solid #ffffff",
                    padding: "3px 2px",
                    color: "#ffffff",
                    fontSize: tableLabelFontSize,
                    fontWeight: 500,
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                    textOverflow: "ellipsis",
                  }}
                  title={tableRowLabel}
                >
                  {tableRowLabel}
                </td>
                {data.map((d, i) => (
                  <td
                    key={i}
                    style={{
                      backgroundColor:
                        i % 2 === 0 ? "transparent" : "#5a5a5b",
                      color: "#ffffff",
                      border: "1px solid #ffffff",
                      padding: "3px 0px",
                      textAlign: "center",
                      fontSize: tableHeaderFontSize,

                      fontWeight: 500,
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {(d.value ?? 0).toLocaleString()}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
          <div
            className="text-center font-semibold mt-2"
            style={{ color: "#ffffff", fontSize: "10px" }}
          >
            TIME
          </div>
        </div>
      </div>
    </div>
  );
};

export default AreaChart;